import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Tasks from './pages/Tasks';
import TaskForm from './components/TaskForm';
import Navbar from './components/Navbar';
import HorizontalNavbar from './components/HorizontalNavbar';
import { Toaster } from 'react-hot-toast';
import Users from './pages/Users';
import Posts from './pages/Posts';
import PostEditor from './components/PostEditor';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Admins from './pages/Admins';
import AdminInvite from './pages/AdminInvite';
import AdminInviteTest from './pages/AdminInviteTest';
import AdminInviteSimple from './pages/AdminInviteSimple';
import LearningPaths from './pages/LearningPaths';
import CreateLearningPath from './pages/CreateLearningPath';

// Public route that redirects to dashboard if user is already authenticated
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  return user ? <Navigate to="/dashboard" replace /> : children;
};

// Protected Route component with updated layout to pass isOpen to HorizontalNavbar
const ProtectedRoute = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen relative">
      {/* Background Elements - Same as Achievements.js */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F0F0F] via-[#1A1A1A] to-[#0F0F0F]" />
        <div className="absolute inset-0 pattern-grid opacity-8" />
        <div className="absolute top-0 right-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] lg:w-[800px] lg:h-[800px] bg-[#FF3366]/15 rounded-full blur-[60px] md:blur-[90px] lg:blur-[120px] transform -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] lg:w-[800px] lg:h-[800px] bg-[#FF6B4D]/15 rounded-full blur-[60px] md:blur-[90px] lg:blur-[120px] transform translate-y-1/4" />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        <Navbar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <HorizontalNavbar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className={`transition-all duration-300 ml-0 pt-16 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-16'}`}>
          <div className="max-w-full px-4 md:px-6 lg:max-w-[1400px] lg:mx-auto lg:p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// App içeriği ayrı component olarak
const AppContent = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task/new"
        element={
          <ProtectedRoute>
            <TaskForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task/edit/:slug"
        element={
          <ProtectedRoute>
            <TaskForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        }
      />
      <Route
        path="/posts"
        element={
          <ProtectedRoute>
            <Posts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/post/new"
        element={
          <ProtectedRoute>
            <PostEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/post/edit/:id"
        element={
          <ProtectedRoute>
            <PostEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admins"
        element={
          <ProtectedRoute>
            <Admins />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invite/:token"
        element={<AdminInvite />}
      />
      <Route
        path="/admin/invite-test/:token"
        element={<AdminInviteTest />}
      />
      <Route
        path="/admin/invite-simple/:token"
        element={<AdminInviteSimple />}
      />
      <Route
        path="/learning-paths"
        element={
          <ProtectedRoute>
            <LearningPaths />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-paths/create"
        element={
          <ProtectedRoute>
            <CreateLearningPath />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning-paths/:id/edit"
        element={
          <ProtectedRoute>
            <CreateLearningPath />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={<Navigate to="/dashboard" replace />}
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" />
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
